import React, { useState } from "react";
import {
  HardDrive,
  Play,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  EyeOff,
} from "lucide-react";
import { PrimaryButton, SecondaryButton } from "../common/ActionButtons";

const BackupManager = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [backupLog, setBackupLog] = useState([]);
  const [lastBackup, setLastBackup] = useState(null);

  // Backup configuration
  const [config, setConfig] = useState({
    server: "78.138.51.30",
    port: 22,
    user: "root",
    remoteDir: "/root/n8n-stack",
    remoteScript: "backup.sh",
    remoteBackupDir: "/root/n8n_backups",
    keep: 7,
    noZip: false,
    noCleanup: false,
  });

  const addLog = (message, type = "info") => {
    const timestamp = new Date().toLocaleTimeString();
    setBackupLog((prev) => [...prev, { timestamp, message, type }]);
  };

  const runBackup = async () => {
    if (!password) {
      addLog("Password is required", "error");
      alert("Please enter the SSH password");
      return;
    }

    setIsRunning(true);
    setBackupLog([]);
    addLog("Starting backup process...", "info");

    try {
      const payload = {
        password: password,
        server: config.server,
        port: config.port,
        user: config.user,
        remoteDir: config.remoteDir,
        remoteScript: config.remoteScript,
        remoteBackupDir: config.remoteBackupDir,
        keep: config.keep,
        noZip: config.noZip,
        noCleanup: config.noCleanup,
      };

      addLog("Connecting to backup server...", "info");

      // Make actual API call to backend
      const response = await fetch("http://localhost:3001/api/n8n/backup/run", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(
          `Server returned ${response.status}: ${response.statusText}`,
        );
      }

      // Read Server-Sent Events stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode the chunk
        const chunk = decoder.decode(value);

        // Parse SSE messages (format: "data: {...}\n\n")
        const lines = chunk.split("\n\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.substring(6));
              addLog(data.message, data.type || "info");

              // Check if this is a success message to capture backup info
              if (
                data.type === "success" &&
                data.message.includes("BACKUP COMPLETED")
              ) {
                const backupInfo = {
                  timestamp: new Date().toISOString(),
                  server: config.server,
                  localPath: `./backups/${config.server}/${new Date().toISOString().split("T")[0].replace(/-/g, "")}`,
                  zipPath: !config.noZip
                    ? `./backups/n8n-backup-${config.server}-${Date.now()}.zip`
                    : null,
                };
                setLastBackup(backupInfo);
              }
            } catch (e) {
              console.error("Failed to parse SSE message:", e);
            }
          }
        }
      }

      addLog("Stream completed", "info");
    } catch (error) {
      console.error("Backup failed:", error);
      addLog(`❌ Backup failed: ${error.message}`, "error");

      if (error.message.includes("Failed to fetch")) {
        addLog(
          "⚠️ Cannot connect to backup server. Make sure backup-server.js is running on port 3001",
          "error",
        );
        addLog("💡 Run: node backup-server.js", "info");
      }
    } finally {
      setIsRunning(false);
      // Clear password after use (security)
      setPassword("");
    }
  };

  const getLogColor = (type) => {
    switch (type) {
      case "success":
        return "text-green-700";
      case "error":
        return "text-red-700";
      case "warning":
        return "text-amber-700";
      default:
        return "text-slate-700";
    }
  };

  const getLogIcon = (type) => {
    switch (type) {
      case "success":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-600" />;
      case "warning":
        return <AlertCircle className="w-4 h-4 text-amber-600" />;
      default:
        return <div className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            N8N Backup Manager
          </h1>
          <p className="text-slate-600">
            Create complete backups of your n8n instance
          </p>
        </div>
        <div className="flex items-center gap-2">
          <HardDrive className="w-8 h-8 text-blue-600" />
        </div>
      </div>

      {/* Configuration Panel */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-800 mb-4">
          Backup Configuration
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Server *
            </label>
            <input
              type="text"
              value={config.server}
              onChange={(e) => setConfig({ ...config, server: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Port
            </label>
            <input
              type="number"
              value={config.port}
              onChange={(e) =>
                setConfig({ ...config, port: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              User
            </label>
            <input
              type="text"
              value={config.user}
              onChange={(e) => setConfig({ ...config, user: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Keep Backups
            </label>
            <input
              type="number"
              value={config.keep}
              onChange={(e) =>
                setConfig({ ...config, keep: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Remote Directory
            </label>
            <input
              type="text"
              value={config.remoteDir}
              onChange={(e) =>
                setConfig({ ...config, remoteDir: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Backup Script
            </label>
            <input
              type="text"
              value={config.remoteScript}
              onChange={(e) =>
                setConfig({ ...config, remoteScript: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              disabled={isRunning}
            />
          </div>
        </div>

        <div className="flex items-center gap-4 mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.noZip}
              onChange={(e) =>
                setConfig({ ...config, noZip: e.target.checked })
              }
              disabled={isRunning}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            />
            <span className="ml-2 text-sm text-slate-700">Skip zip bundle</span>
          </label>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={config.noCleanup}
              onChange={(e) =>
                setConfig({ ...config, noCleanup: e.target.checked })
              }
              disabled={isRunning}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            />
            <span className="ml-2 text-sm text-slate-700">
              Skip cleanup (keep all backups)
            </span>
          </label>
        </div>

        {/* Password Input */}
        <div className="border-t border-slate-200 pt-4">
          <label className="block text-sm font-medium text-slate-700 mb-1">
            SSH Password *
          </label>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter SSH password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 pr-10"
                disabled={isRunning}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
            <PrimaryButton
              onClick={runBackup}
              disabled={isRunning || !password}
              icon={isRunning ? RefreshCw : Play}
            >
              {isRunning ? "Running..." : "Start Backup"}
            </PrimaryButton>
          </div>
          <p className="text-xs text-slate-500 mt-1">
            Password is required for SSH authentication. It will not be stored.
          </p>
        </div>
      </div>

      {/* Last Backup Info */}
      {lastBackup && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-green-900 mb-1">
                Last Backup Completed
              </h3>
              <p className="text-sm text-green-800">
                <strong>Time:</strong>{" "}
                {new Date(lastBackup.timestamp).toLocaleString()}
              </p>
              <p className="text-sm text-green-800">
                <strong>Server:</strong> {lastBackup.server}
              </p>
              <p className="text-sm text-green-800">
                <strong>Location:</strong> {lastBackup.localPath}
              </p>
              {lastBackup.zipPath && (
                <p className="text-sm text-green-800">
                  <strong>Zip:</strong> {lastBackup.zipPath}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Backup Log */}
      <div className="bg-white rounded-lg shadow-md border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Backup Log</h3>
          {backupLog.length > 0 && (
            <SecondaryButton
              onClick={() => setBackupLog([])}
              disabled={isRunning}
            >
              Clear Log
            </SecondaryButton>
          )}
        </div>
        <div className="p-4 bg-slate-900 rounded-b-lg min-h-[300px] max-h-[500px] overflow-y-auto font-mono text-sm">
          {backupLog.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <HardDrive className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No backup running. Click "Start Backup" to begin.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {backupLog.map((log, index) => (
                <div
                  key={index}
                  className="flex items-start gap-2 text-slate-300"
                >
                  <span className="text-slate-500 shrink-0">
                    [{log.timestamp}]
                  </span>
                  {getLogIcon(log.type)}
                  <span className={getLogColor(log.type)}>{log.message}</span>
                </div>
              ))}
              {isRunning && (
                <div className="flex items-center gap-2 text-blue-400 animate-pulse">
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <span>Processing...</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">
            📦 What Gets Backed Up
          </h3>
          <ul className="text-sm text-blue-800 space-y-1 ml-4 list-disc">
            <li>Latest backup from remote server</li>
            <li>Docker compose.yml file</li>
            <li>.env configuration file</li>
            <li>/root/.n8n directory</li>
            <li>Container config mounts</li>
            <li>Backup manifest (JSON)</li>
            {!config.noZip && <li>Zip bundle of all files</li>}
          </ul>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-amber-900 mb-2">
            ⚙️ Backend Setup Required
          </h3>
          <ul className="text-sm text-amber-800 space-y-1 ml-4 list-disc">
            <li>
              Create API endpoint:{" "}
              <code className="bg-amber-100 px-1 rounded">
                /api/n8n/backup/run
              </code>
            </li>
            <li>
              Script location:{" "}
              <code className="bg-amber-100 px-1 rounded">
                src/utils/run_n8n_backup.ps1
              </code>
            </li>
            <li>Install Posh-SSH PowerShell module</li>
            <li>Execute script from project root</li>
            <li>Stream output back to frontend</li>
            <li>Return backup manifest and paths</li>
          </ul>
        </div>

        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-green-900 mb-2">
            ✅ How It Works
          </h3>
          <ul className="text-sm text-green-800 space-y-1 ml-4 list-decimal">
            <li>Connect to remote server via SSH</li>
            <li>Run backup.sh script on server</li>
            <li>Download latest backup files</li>
            <li>Download compose, .env, configs</li>
            <li>Create manifest.json</li>
            <li>Bundle into zip (optional)</li>
            <li>Clean up old backups (optional)</li>
          </ul>
        </div>

        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-purple-900 mb-2">
            🔒 Security Notes
          </h3>
          <ul className="text-sm text-purple-800 space-y-1 ml-4 list-disc">
            <li>Password is NOT stored anywhere</li>
            <li>Only transmitted once per backup</li>
            <li>Uses secure SSH connection</li>
            <li>Backups stored locally only</li>
            <li>Credentials not included in backup</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default BackupManager;
